import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Gift, Check, CalendarDays, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { useConfig, symbolForCurrency, formatMoney, setMoneyLocale } from "../lib/format";

const inputCls = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold";

// Public (no login): participant confirms attendance and tells which gift they want, within the group limit.
export default function SecretSantaConfirm() {
  const { t, lang } = useI18n();
  const { token } = useParams();
  const config = useConfig();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [wish, setWish] = useState("");
  const [link, setLink] = useState("");
  const [dislikes, setDislikes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setMoneyLocale(lang);
    api.get(`/secret-santa/invite/${token}`).then(({ data }) => {
      setInfo(data); setWish(data.wish || ""); setLink(data.wish_link || ""); setDislikes(data.dislikes || ""); setDone(!!data.confirmed);
    }).catch(() => setError(true));
  }, [token, lang]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-xmas-navy text-xmas-slate" data-testid="ss-confirm-invalid">{t("ss.reveal_invalid")}</div>;
  if (!info) return <div className="flex min-h-screen items-center justify-center bg-xmas-navy"><div className="h-10 w-10 animate-spin rounded-full border-2 border-xmas-gold border-t-transparent" /></div>;

  const sym = symbolForCurrency(config, info.currency);
  const limit = info.suggested_value ? formatMoney(sym, info.suggested_value) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (wish.trim().length < 2) { toast.error(t("ss.wish_required")); return; }
    setSaving(true);
    try {
      await api.post(`/secret-santa/invite/${token}/confirm`, { wish: wish.trim(), wish_link: link.trim() || null, dislikes: dislikes.trim() || null });
      setDone(true);
      toast.success(t("ss.confirmed_toast"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-xmas-navy px-4 py-10">
      <div className="glass w-full max-w-md rounded-3xl p-8" data-testid="ss-confirm-page">
        <p className="text-center text-[11px] uppercase tracking-widest text-xmas-gold">MY CHRISTMAS · {info.group_name}</p>
        <h1 className="mt-3 text-center font-display text-2xl text-xmas-snow">{t("ss.reveal_hello", { name: info.participant_name })}</h1>
        <p className="mt-2 text-center text-sm text-xmas-slate">{t("ss.confirm_intro", { owner: info.owner_name || "", group: info.group_name })}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3 text-xs text-xmas-champagne">
          {limit && <span className="inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/30 px-3 py-1.5" data-testid="ss-confirm-limit"><Wallet className="h-3.5 w-3.5" /> {t("ss.gift_limit", { v: limit })}</span>}
          {info.event_date && <span className="inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/30 px-3 py-1.5"><CalendarDays className="h-3.5 w-3.5" /> {new Date(info.event_date + "T12:00:00").toLocaleDateString(lang === "pt" ? "pt-BR" : lang)}</span>}
        </div>

        {info.drawn ? (
          <p className="mt-8 text-center text-sm text-xmas-slate" data-testid="ss-confirm-drawn">{t("ss.confirm_drawn")}</p>
        ) : done ? (
          <div className="mt-8 text-center" data-testid="ss-confirm-done">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-500/15"><Check className="h-7 w-7 text-green-400" /></div>
            <p className="mt-4 font-display text-lg text-xmas-snow">{t("ss.confirmed_title")}</p>
            <p className="mt-2 text-sm text-xmas-slate">{t("ss.confirmed_text")}</p>
            <div className="mt-5 rounded-2xl bg-xmas-navy/50 p-4 text-left text-sm">
              <p className="text-xs uppercase tracking-widest text-xmas-gold">{t("ss.your_wish")}</p>
              <p className="mt-1 text-xmas-snow" data-testid="ss-confirm-wish">{wish}</p>
              {link && <a href={link} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-xmas-gold underline">{link}</a>}
            </div>
            <button onClick={() => setDone(false)} data-testid="ss-confirm-edit" className="mt-4 text-xs text-xmas-gold hover:underline">{t("ss.edit_wish")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.wish_label")}{limit ? ` · ${t("ss.up_to", { v: limit })}` : ""}</label>
              <input value={wish} onChange={(e) => setWish(e.target.value)} maxLength={160} placeholder={t("ss.wish_ph")} data-testid="ss-confirm-wish-input" className={inputCls} />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.wish_link_label")}</label>
              <input value={link} onChange={(e) => setLink(e.target.value)} maxLength={500} placeholder="https://" data-testid="ss-confirm-link-input" className={inputCls} />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.dislikes")}</label>
              <input value={dislikes} onChange={(e) => setDislikes(e.target.value)} maxLength={160} data-testid="ss-confirm-dislikes-input" className={inputCls} />
            </div>
            <button type="submit" disabled={saving} data-testid="ss-confirm-submit" className="btn-gold inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
              <Gift className="h-4 w-4" /> {saving ? "..." : t("ss.confirm_btn")}
            </button>
            <p className="text-center text-[11px] text-xmas-slate">{t("ss.confirm_hint")}</p>
          </form>
        )}
      </div>
    </div>
  );
}
